import { useEffect, useMemo, useState } from 'react';
import { Button, Dialog, EmptyState, Input, Label, Select } from '../components/ui';
import {
    adminErrorMessage,
    dependencySummary,
    dependencyTotal,
    useAddServiceCommunityAlias,
    useAdminBuilders,
    useAdminCommunities,
    useAdminCrews,
    useAdminServiceCommunities,
    useDeleteServiceCommunityAlias,
    useRecordDependencies,
    useServiceSections,
    useSetRecordActive,
    useUpsertBuilder,
    useUpsertCommunity,
    useUpsertCrew,
    useUpsertServiceCommunity,
    type AdminBuilder,
    type AdminCommunity,
    type AdminCrew,
    type AdminEntity,
    type AdminServiceCommunity,
    type CrewKind,
    type Dependencies,
} from '../lib/adminRecords';

/**
 * Phase 10 — Community, Crew and Builder Admin.
 *
 * Four record sets the office maintains itself: builders, scheduling
 * communities, crews, and service communities with their spreadsheet aliases.
 *
 * Two rules run through the whole page.
 *
 * Nothing is ever deleted. There is no delete control, because a community with
 * jobs or a crew with service items would take that history with it. Records go
 * inactive, which takes them out of every picker and leaves the past intact.
 *
 * A record with live work attached will not switch off on one click. The count
 * is shown beside the switch before it is touched, and switching off then needs
 * a second, deliberate confirm. That follows the service board's delete control
 * rather than inventing a second pattern: arm, then confirm, and disarm the
 * moment attention moves to another row so a stray second click cannot land
 * somewhere else.
 *
 * Communities always ask first, attached work or not. create_job_from_jobtread
 * only resolves a PO against an active community, so switching one off stops
 * its new purchase orders reaching the board. The confirm only forces past the
 * live work check when that count was actually on screen, so a count that had
 * not loaded yet is still refused by the database rather than skipped.
 *
 * Route sections are read only here. Managing the sections themselves, merging
 * records, bulk import and pushing records back to JobTread are out of scope for
 * this milestone and quoted separately.
 */

type Tab = 'builders' | 'communities' | 'crews' | 'service';

const TABS: { id: Tab; label: string }[] = [
    { id: 'builders', label: 'Builders' },
    { id: 'communities', label: 'Communities' },
    { id: 'crews', label: 'Crews' },
    { id: 'service', label: 'Service communities' },
];

export function AdminPage() {
    const [tab, setTab] = useState<Tab>('builders');

    return (
        <div className="p-4 sm:p-6 max-w-5xl">
            <h1 className="text-lg font-semibold text-graphite-900 dark:text-graphite-50">
                Records
            </h1>
            <p className="mt-1 mb-5 text-sm text-graphite-600 dark:text-graphite-400 max-w-prose">
                Builders, communities and crews the rest of the app is built on. Records are
                switched off rather than deleted, so anything they are attached to keeps its
                history.
            </p>

            <div
                role="tablist"
                className="flex flex-wrap gap-1 border-b border-graphite-300 dark:border-graphite-700"
            >
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        role="tab"
                        aria-selected={tab === t.id}
                        onClick={() => setTab(t.id)}
                        className={
                            'px-3 py-2 text-sm font-medium -mb-px border-b-2 transition-colors ' +
                            (tab === t.id
                                ? 'border-signal-500 text-graphite-900 dark:text-graphite-50'
                                : 'border-transparent text-graphite-600 hover:text-graphite-900 dark:text-graphite-400 dark:hover:text-graphite-100')
                        }
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            <div className="pt-5">
                {tab === 'builders' && <BuildersTab />}
                {tab === 'communities' && <CommunitiesTab />}
                {tab === 'crews' && <CrewsTab />}
                {tab === 'service' && <ServiceCommunitiesTab />}
            </div>
        </div>
    );
}

// -----------------------------------------------------------------------------
// Shared pieces
// -----------------------------------------------------------------------------

function ErrorBanner({ message, onDismiss }: { message: string; onDismiss: () => void }) {
    return (
        <div className="mb-4 px-3 py-2 rounded border border-red-500/40 bg-red-500/10 flex items-start gap-3">
            <p className="flex-1 text-sm text-red-700 dark:text-red-300">{message}</p>
            <button
                onClick={onDismiss}
                className="text-xs text-red-700 dark:text-red-300 hover:underline shrink-0"
            >
                Dismiss
            </button>
        </div>
    );
}

function TabHeader({
    count,
    noun,
    onAdd,
}: {
    count: number;
    noun: string;
    onAdd: () => void;
}) {
    return (
        <div className="flex items-center justify-between gap-3 mb-3">
            <p className="text-sm text-graphite-600 dark:text-graphite-400">
                {count} {count === 1 ? noun : noun + 's'}
            </p>
            <Button variant="primary" size="sm" onClick={onAdd}>
                Add {noun}
            </Button>
        </div>
    );
}

/**
 * One record. The switch and its confirm live here so the armed state belongs to
 * a single row and cannot be inherited by the next one.
 */
function RecordRow({
    title,
    detail,
    active,
    deps,
    armed,
    busy,
    onEdit,
    onArm,
    onDisarm,
    onSetActive,
    offWarning,
    children,
}: {
    title: string;
    detail: React.ReactNode;
    active: boolean;
    deps: Dependencies | undefined;
    armed: boolean;
    busy: boolean;
    onEdit: () => void;
    onArm: () => void;
    onDisarm: () => void;
    onSetActive: (active: boolean, force: boolean) => void;
    /**
     * Shown before this record switches off even when nothing is attached, and
     * makes the switch always ask first. Communities use it: switched off, their
     * new purchase orders stop reaching the board.
     */
    offWarning?: string;
    children?: React.ReactNode;
}) {
    const total = dependencyTotal(deps);
    const summary = dependencySummary(deps);

    return (
        <div
            className={
                'px-3 py-3 border-b border-graphite-200 dark:border-graphite-800 ' +
                (active ? '' : 'opacity-60')
            }
        >
            <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[12rem]">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-graphite-900 dark:text-graphite-50">
                            {title}
                        </span>
                        {!active && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-graphite-300 text-graphite-800 dark:bg-graphite-700 dark:text-graphite-200">
                                Off
                            </span>
                        )}
                    </div>
                    <div className="mt-0.5 text-xs text-graphite-600 dark:text-graphite-400">
                        {detail}
                    </div>
                    {active && total > 0 && (
                        <p className="mt-1 text-xs text-graphite-500 dark:text-graphite-500">
                            {summary}
                        </p>
                    )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                    <Button size="sm" variant="ghost" onClick={onEdit} disabled={busy}>
                        Edit
                    </Button>

                    {active ? (
                        armed ? (
                            <>
                                <Button
                                    size="sm"
                                    variant="danger"
                                    disabled={busy}
                                    onClick={() => onSetActive(false, total > 0)}
                                >
                                    Switch off anyway
                                </Button>
                                <Button size="sm" variant="ghost" onClick={onDisarm}>
                                    Keep on
                                </Button>
                            </>
                        ) : (
                            <Button
                                size="sm"
                                disabled={busy}
                                onClick={() =>
                                    total > 0 || offWarning ? onArm() : onSetActive(false, false)
                                }
                            >
                                Switch off
                            </Button>
                        )
                    ) : (
                        <Button size="sm" disabled={busy} onClick={() => onSetActive(true, false)}>
                            Switch on
                        </Button>
                    )}
                </div>
            </div>

            {armed && (total > 0 || offWarning) && (
                <p className="mt-2 text-xs text-amber-700 dark:text-amber-400 max-w-prose">
                    {total > 0 && (
                        <>
                            {title} still has {summary}. Switching it off takes it out of every
                            dropdown while that work stays attached to it.{' '}
                        </>
                    )}
                    {offWarning}
                </p>
            )}

            {children}
        </div>
    );
}

/** Arm state plus the set-active call, shared by all four tabs. */
function useActiveSwitch(entity: AdminEntity, onError: (m: string) => void) {
    const [armedId, setArmedId] = useState<string | null>(null);
    const setActive = useSetRecordActive();

    return {
        armedId,
        arm: (id: string) => setArmedId(id),
        disarm: () => setArmedId(null),
        busy: setActive.isPending,
        run: (id: string, active: boolean, force: boolean) => {
            setActive.mutate(
                { p_entity: entity, p_id: id, p_active: active, p_force: force },
                {
                    onSuccess: () => setArmedId(null),
                    onError: (e) => {
                        onError(adminErrorMessage(e));
                        setArmedId(null);
                    },
                }
            );
        },
    };
}

function Field({
    label,
    hint,
    children,
}: {
    label: string;
    hint?: string;
    children: React.ReactNode;
}) {
    return (
        <div className="mb-3">
            <Label>{label}</Label>
            {children}
            {hint && (
                <p className="mt-1 text-xs text-graphite-500 dark:text-graphite-500">{hint}</p>
            )}
        </div>
    );
}

// -----------------------------------------------------------------------------
// Builders
// -----------------------------------------------------------------------------

function BuildersTab() {
    const { data: rows, isLoading } = useAdminBuilders();
    const { data: deps } = useRecordDependencies();
    const [error, setError] = useState('');
    const [editing, setEditing] = useState<AdminBuilder | null | undefined>(undefined);
    const sw = useActiveSwitch('builder', setError);

    const list = rows ?? [];

    return (
        <>
            {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
            <TabHeader count={list.length} noun="builder" onAdd={() => setEditing(null)} />

            {isLoading ? (
                <p className="text-sm text-graphite-500">Loading builders.</p>
            ) : list.length === 0 ? (
                <EmptyState
                    title="No builders yet"
                    description="Add the builders you pour for. Each needs a two or three letter prefix, which the board shows beside every one of its lots."
                />
            ) : (
                <div className="border-t border-graphite-200 dark:border-graphite-800">
                    {list.map((b) => (
                        <RecordRow
                            key={b.id}
                            title={b.name}
                            detail={
                                <>
                                    Prefix {b.prefix}
                                    {b.foundation_cost_code
                                        ? ` · foundation cost code ${b.foundation_cost_code}`
                                        : ''}
                                    {b.qbo_customer_name ? ` · QuickBooks ${b.qbo_customer_name}` : ''}
                                </>
                            }
                            active={b.active}
                            deps={deps?.builders?.[b.id]}
                            armed={sw.armedId === b.id}
                            busy={sw.busy}
                            onEdit={() => {
                                sw.disarm();
                                setEditing(b);
                            }}
                            onArm={() => sw.arm(b.id)}
                            onDisarm={sw.disarm}
                            onSetActive={(a, f) => sw.run(b.id, a, f)}
                        />
                    ))}
                </div>
            )}

            {editing !== undefined && (
                <BuilderDialog
                    builder={editing}
                    onClose={() => setEditing(undefined)}
                    onError={setError}
                />
            )}
        </>
    );
}

function BuilderDialog({
    builder,
    onClose,
    onError,
}: {
    builder: AdminBuilder | null;
    onClose: () => void;
    onError: (m: string) => void;
}) {
    const save = useUpsertBuilder();
    const [name, setName] = useState(builder?.name ?? '');
    const [prefix, setPrefix] = useState(builder?.prefix ?? '');
    const [ref, setRef] = useState(builder?.external_ref ?? '');
    const [qbo, setQbo] = useState(builder?.qbo_customer_name ?? '');
    const [code, setCode] = useState(builder?.foundation_cost_code ?? '');
    const [local, setLocal] = useState('');

    function submit() {
        setLocal('');
        save.mutate(
            {
                p_id: builder?.id ?? null,
                p_name: name,
                p_prefix: prefix,
                p_external_ref: ref || null,
                p_qbo_customer_name: qbo || null,
                p_foundation_cost_code: code || null,
            },
            { onSuccess: onClose, onError: (e) => setLocal(adminErrorMessage(e)) }
        );
    }

    useEffect(() => {
        if (local) onError('');
    }, [local, onError]);

    return (
        <Dialog
            open
            onClose={onClose}
            title={builder ? 'Edit builder' : 'Add builder'}
            subtitle={builder?.name}
            footer={
                <>
                    <Button variant="ghost" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={submit} disabled={save.isPending}>
                        {save.isPending ? 'Saving' : 'Save builder'}
                    </Button>
                </>
            }
        >
            {local && <ErrorBanner message={local} onDismiss={() => setLocal('')} />}
            <Field label="Name">
                <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            </Field>
            <Field
                label="Prefix"
                hint="Two or three letters, for example KH or DRH. The board shows it beside every one of this builder's lots."
            >
                <Input
                    value={prefix}
                    maxLength={3}
                    onChange={(e) => setPrefix(e.target.value.toUpperCase())}
                />
            </Field>
            <Field label="Foundation cost code" hint="Optional.">
                <Input value={code} onChange={(e) => setCode(e.target.value)} />
            </Field>
            <Field label="QuickBooks customer name" hint="Optional.">
                <Input value={qbo} onChange={(e) => setQbo(e.target.value)} />
            </Field>
            <Field label="Reference" hint="Optional.">
                <Input value={ref} onChange={(e) => setRef(e.target.value)} />
            </Field>
        </Dialog>
    );
}

// -----------------------------------------------------------------------------
// Communities
// -----------------------------------------------------------------------------

function CommunitiesTab() {
    const { data: rows, isLoading } = useAdminCommunities();
    const { data: builders } = useAdminBuilders();
    const { data: deps } = useRecordDependencies();
    const [error, setError] = useState('');
    const [editing, setEditing] = useState<AdminCommunity | null | undefined>(undefined);
    const [builderFilter, setBuilderFilter] = useState('');
    const sw = useActiveSwitch('community', setError);

    const list = useMemo(
        () => (rows ?? []).filter((c) => !builderFilter || c.builder_id === builderFilter),
        [rows, builderFilter]
    );

    return (
        <>
            {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

            <div className="flex items-end justify-between gap-3 mb-3 flex-wrap">
                <div>
                    <Label htmlFor="builderFilter">Builder</Label>
                    <Select
                        id="builderFilter"
                        value={builderFilter}
                        onChange={(e) => setBuilderFilter(e.target.value)}
                    >
                        <option value="">All builders</option>
                        {(builders ?? []).map((b) => (
                            <option key={b.id} value={b.id}>
                                {b.name}
                            </option>
                        ))}
                    </Select>
                </div>
                <div className="flex items-center gap-3">
                    <p className="text-sm text-graphite-600 dark:text-graphite-400">
                        {list.length} {list.length === 1 ? 'community' : 'communities'}
                    </p>
                    <Button variant="primary" size="sm" onClick={() => setEditing(null)}>
                        Add community
                    </Button>
                </div>
            </div>

            {isLoading ? (
                <p className="text-sm text-graphite-500">Loading communities.</p>
            ) : list.length === 0 ? (
                <EmptyState
                    title="No communities here"
                    description="Add a community, or clear the builder filter to see the rest."
                />
            ) : (
                <div className="border-t border-graphite-200 dark:border-graphite-800">
                    {list.map((c) => (
                        <RecordRow
                            key={c.id}
                            title={c.name}
                            detail={
                                <>
                                    {c.builders?.name ?? 'No builder'}
                                    {c.jt_location_id ? (
                                        <> · JobTread {c.jt_location_id}</>
                                    ) : (
                                        <span className="text-amber-700 dark:text-amber-400">
                                            {' '}
                                            · no JobTread location, purchase orders for this
                                            community will not land
                                        </span>
                                    )}
                                </>
                            }
                            active={c.active}
                            deps={deps?.communities?.[c.id]}
                            armed={sw.armedId === c.id}
                            busy={sw.busy}
                            onEdit={() => {
                                sw.disarm();
                                setEditing(c);
                            }}
                            onArm={() => sw.arm(c.id)}
                            onDisarm={sw.disarm}
                            onSetActive={(a, f) => sw.run(c.id, a, f)}
                            offWarning="While it is switched off, new purchase orders for this community will not reach the board."
                        />
                    ))}
                </div>
            )}

            {editing !== undefined && (
                <CommunityDialog
                    community={editing}
                    onClose={() => setEditing(undefined)}
                    onError={setError}
                />
            )}
        </>
    );
}

/**
 * A latitude or longitude as typed. Blank means none. Anything else has to be a
 * plain decimal. Number() alone turns a typo into NaN, and NaN is sent as null,
 * which would quietly clear the community's coordinates and move its weather
 * records off the site.
 */
function parseCoordinate(
    raw: string,
    label: string,
    example: string
): { value: number | null; error: string } {
    const t = raw.trim();
    if (t === '') return { value: null, error: '' };
    if (!/^[+-]?(\d+(\.\d*)?|\.\d+)$/.test(t)) {
        return { value: null, error: `${label} has to be a plain number, like ${example}.` };
    }
    return { value: Number(t), error: '' };
}

function CommunityDialog({
    community,
    onClose,
    onError,
}: {
    community: AdminCommunity | null;
    onClose: () => void;
    onError: (m: string) => void;
}) {
    const save = useUpsertCommunity();
    const { data: builders } = useAdminBuilders();
    const [builderId, setBuilderId] = useState(community?.builder_id ?? '');
    const [name, setName] = useState(community?.name ?? '');
    const [jt, setJt] = useState(community?.jt_location_id ?? '');
    const [lat, setLat] = useState(community?.latitude?.toString() ?? '');
    const [lng, setLng] = useState(community?.longitude?.toString() ?? '');
    const [ref, setRef] = useState(community?.external_ref ?? '');
    const [local, setLocal] = useState('');

    const jtChanged = Boolean(community) && jt !== (community?.jt_location_id ?? '');

    function submit() {
        setLocal('');
        const la = parseCoordinate(lat, 'Latitude', '35.2271');
        const lo = parseCoordinate(lng, 'Longitude', '-80.8431');
        if (la.error || lo.error) {
            setLocal(la.error || lo.error);
            return;
        }
        save.mutate(
            {
                p_id: community?.id ?? null,
                p_builder_id: builderId || null,
                p_name: name,
                p_external_ref: ref || null,
                p_jt_location_id: jt || null,
                p_latitude: la.value,
                p_longitude: lo.value,
            },
            { onSuccess: onClose, onError: (e) => setLocal(adminErrorMessage(e)) }
        );
    }

    useEffect(() => {
        if (local) onError('');
    }, [local, onError]);

    return (
        <Dialog
            open
            onClose={onClose}
            title={community ? 'Edit community' : 'Add community'}
            subtitle={community?.name}
            footer={
                <>
                    <Button variant="ghost" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={submit} disabled={save.isPending}>
                        {save.isPending ? 'Saving' : 'Save community'}
                    </Button>
                </>
            }
        >
            {local && <ErrorBanner message={local} onDismiss={() => setLocal('')} />}
            <Field label="Builder">
                <Select value={builderId} onChange={(e) => setBuilderId(e.target.value)}>
                    <option value="">Pick a builder</option>
                    {(builders ?? []).map((b) => (
                        <option key={b.id} value={b.id}>
                            {b.name}
                        </option>
                    ))}
                </Select>
            </Field>
            <Field label="Name">
                <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            </Field>
            <Field
                label="JobTread location"
                hint="The location purchase orders for this community are matched to. Leave it blank and those POs come back as location not configured."
            >
                <Input
                    value={jt}
                    onChange={(e) => setJt(e.target.value)}
                    placeholder="22PUNddL96jN"
                />
            </Field>
            {jtChanged && (
                <div className="mb-3 px-3 py-2 rounded border border-amber-500/40 bg-amber-500/10">
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                        Changing the JobTread location changes where every future purchase order
                        for this community is filed. Check the id against JobTread before saving.
                    </p>
                </div>
            )}
            <div className="grid grid-cols-2 gap-3">
                <Field label="Latitude" hint="Where this community's weather is pulled from.">
                    <Input value={lat} onChange={(e) => setLat(e.target.value)} inputMode="decimal" />
                </Field>
                <Field label="Longitude" hint="Fill in both, as plain numbers.">
                    <Input value={lng} onChange={(e) => setLng(e.target.value)} inputMode="decimal" />
                </Field>
            </div>
            <Field label="Reference" hint="Optional.">
                <Input value={ref} onChange={(e) => setRef(e.target.value)} />
            </Field>
        </Dialog>
    );
}

// -----------------------------------------------------------------------------
// Crews
// -----------------------------------------------------------------------------

const CREW_KINDS: { value: CrewKind; label: string }[] = [
    { value: 'pour', label: 'Pour' },
    { value: 'service', label: 'Service' },
    { value: 'both', label: 'Pour and service' },
];

function CrewsTab() {
    const { data: rows, isLoading } = useAdminCrews();
    const { data: deps } = useRecordDependencies();
    const [error, setError] = useState('');
    const [editing, setEditing] = useState<AdminCrew | null | undefined>(undefined);
    const sw = useActiveSwitch('crew', setError);

    const list = rows ?? [];

    return (
        <>
            {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
            <TabHeader count={list.length} noun="crew" onAdd={() => setEditing(null)} />

            {isLoading ? (
                <p className="text-sm text-graphite-500">Loading crews.</p>
            ) : list.length === 0 ? (
                <EmptyState
                    title="No crews yet"
                    description="Add a crew to assign work to it and to email it a day's route."
                />
            ) : (
                <div className="border-t border-graphite-200 dark:border-graphite-800">
                    {list.map((k) => (
                        <RecordRow
                            key={k.id}
                            title={k.name}
                            detail={
                                <>
                                    {CREW_KINDS.find((c) => c.value === k.kind)?.label ?? k.kind}
                                    {k.email ? (
                                        <> · {k.email}</>
                                    ) : (
                                        <span className="text-amber-700 dark:text-amber-400">
                                            {' '}
                                            · no email, this crew cannot be sent a route
                                        </span>
                                    )}
                                </>
                            }
                            active={k.active}
                            deps={deps?.crews?.[k.id]}
                            armed={sw.armedId === k.id}
                            busy={sw.busy}
                            onEdit={() => {
                                sw.disarm();
                                setEditing(k);
                            }}
                            onArm={() => sw.arm(k.id)}
                            onDisarm={sw.disarm}
                            onSetActive={(a, f) => sw.run(k.id, a, f)}
                        />
                    ))}
                </div>
            )}

            {editing !== undefined && (
                <CrewDialog crew={editing} onClose={() => setEditing(undefined)} onError={setError} />
            )}
        </>
    );
}

function CrewDialog({
    crew,
    onClose,
    onError,
}: {
    crew: AdminCrew | null;
    onClose: () => void;
    onError: (m: string) => void;
}) {
    const save = useUpsertCrew();
    const [name, setName] = useState(crew?.name ?? '');
    const [kind, setKind] = useState<CrewKind>(crew?.kind ?? 'pour');
    const [email, setEmail] = useState(crew?.email ?? '');
    const [local, setLocal] = useState('');

    function submit() {
        setLocal('');
        save.mutate(
            {
                p_id: crew?.id ?? null,
                p_name: name,
                p_kind: kind,
                p_email: email || null,
            },
            { onSuccess: onClose, onError: (e) => setLocal(adminErrorMessage(e)) }
        );
    }

    useEffect(() => {
        if (local) onError('');
    }, [local, onError]);

    return (
        <Dialog
            open
            onClose={onClose}
            title={crew ? 'Edit crew' : 'Add crew'}
            subtitle={crew?.name}
            footer={
                <>
                    <Button variant="ghost" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={submit} disabled={save.isPending}>
                        {save.isPending ? 'Saving' : 'Save crew'}
                    </Button>
                </>
            }
        >
            {local && <ErrorBanner message={local} onDismiss={() => setLocal('')} />}
            <Field label="Name">
                <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            </Field>
            <Field label="Works on" hint="Which boards this crew appears on.">
                <Select value={kind} onChange={(e) => setKind(e.target.value as CrewKind)}>
                    {CREW_KINDS.map((c) => (
                        <option key={c.value} value={c.value}>
                            {c.label}
                        </option>
                    ))}
                </Select>
            </Field>
            <Field
                label="Email"
                hint="Where a day's route is sent. Leave it blank and this crew cannot be emailed one."
            >
                <Input
                    value={email}
                    type="email"
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="crew@example.com"
                />
            </Field>
        </Dialog>
    );
}

// -----------------------------------------------------------------------------
// Service communities and their aliases
// -----------------------------------------------------------------------------

function ServiceCommunitiesTab() {
    const { data: rows, isLoading } = useAdminServiceCommunities();
    const { data: deps } = useRecordDependencies();
    const [error, setError] = useState('');
    const [editing, setEditing] = useState<AdminServiceCommunity | null | undefined>(undefined);
    const [openAliases, setOpenAliases] = useState<string | null>(null);
    const sw = useActiveSwitch('service_community', setError);

    const list = rows ?? [];

    return (
        <>
            {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}
            <TabHeader count={list.length} noun="service community" onAdd={() => setEditing(null)} />

            {isLoading ? (
                <p className="text-sm text-graphite-500">Loading service communities.</p>
            ) : list.length === 0 ? (
                <EmptyState
                    title="No service communities yet"
                    description="These are the communities the service board groups by, and the spreadsheet names that map onto them."
                />
            ) : (
                <div className="border-t border-graphite-200 dark:border-graphite-800">
                    {list.map((sc) => {
                        const aliases = sc.service_community_aliases ?? [];
                        return (
                            <RecordRow
                                key={sc.id}
                                title={sc.display_name}
                                detail={
                                    <>
                                        {sc.service_sections?.name
                                            ? `Route section ${sc.service_sections.name}`
                                            : 'No route section'}
                                        {' · '}
                                        <button
                                            onClick={() =>
                                                setOpenAliases(openAliases === sc.id ? null : sc.id)
                                            }
                                            className="underline hover:text-graphite-900 dark:hover:text-graphite-100"
                                        >
                                            {aliases.length}{' '}
                                            {aliases.length === 1
                                                ? 'spreadsheet name'
                                                : 'spreadsheet names'}
                                        </button>
                                    </>
                                }
                                active={sc.active}
                                deps={deps?.service_communities?.[sc.id]}
                                armed={sw.armedId === sc.id}
                                busy={sw.busy}
                                onEdit={() => {
                                    sw.disarm();
                                    setEditing(sc);
                                }}
                                onArm={() => sw.arm(sc.id)}
                                onDisarm={sw.disarm}
                                onSetActive={(a, f) => sw.run(sc.id, a, f)}
                            >
                                {openAliases === sc.id && (
                                    <AliasEditor community={sc} onError={setError} />
                                )}
                            </RecordRow>
                        );
                    })}
                </div>
            )}

            {editing !== undefined && (
                <ServiceCommunityDialog
                    community={editing}
                    onClose={() => setEditing(undefined)}
                    onError={setError}
                />
            )}
        </>
    );
}

/**
 * Spreadsheet names that map onto one service community. These are add and
 * remove rather than switch off: an alias holds no history, it only decides
 * what a typed name on an incoming sheet matches, so removing a wrong one
 * affects future rows and nothing already recorded.
 */
function AliasEditor({
    community,
    onError,
}: {
    community: AdminServiceCommunity;
    onError: (m: string) => void;
}) {
    const add = useAddServiceCommunityAlias();
    const del = useDeleteServiceCommunityAlias();
    const [value, setValue] = useState('');
    const aliases = community.service_community_aliases ?? [];

    function submit() {
        if (!value.trim()) return;
        add.mutate(
            { p_community_id: community.id, p_alias: value },
            { onSuccess: () => setValue(''), onError: (e) => onError(adminErrorMessage(e)) }
        );
    }

    return (
        <div className="mt-3 ml-1 pl-3 border-l-2 border-graphite-300 dark:border-graphite-700">
            <p className="text-xs text-graphite-600 dark:text-graphite-400 max-w-prose">
                Names that appear on incoming sheets for this community. Spacing and capitals are
                ignored when matching.
            </p>

            {aliases.length > 0 && (
                <ul className="mt-2 flex flex-wrap gap-1.5">
                    {aliases.map((a) => (
                        <li
                            key={a.id}
                            className="inline-flex items-center gap-1.5 pl-2 pr-1 py-0.5 rounded bg-graphite-200 dark:bg-graphite-800"
                        >
                            <span className="text-xs text-graphite-800 dark:text-graphite-200">
                                {a.alias}
                            </span>
                            <button
                                aria-label={`Remove ${a.alias}`}
                                disabled={del.isPending}
                                onClick={() =>
                                    del.mutate(
                                        { p_alias_id: a.id },
                                        { onError: (e) => onError(adminErrorMessage(e)) }
                                    )
                                }
                                className="text-xs px-1 text-graphite-600 hover:text-red-600 dark:text-graphite-400 dark:hover:text-red-400"
                            >
                                &times;
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            <div className="mt-2 flex items-center gap-2">
                <Input
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder="Name as it appears on the sheet"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') submit();
                    }}
                />
                <Button size="sm" onClick={submit} disabled={add.isPending || !value.trim()}>
                    Add name
                </Button>
            </div>
        </div>
    );
}

function ServiceCommunityDialog({
    community,
    onClose,
    onError,
}: {
    community: AdminServiceCommunity | null;
    onClose: () => void;
    onError: (m: string) => void;
}) {
    const save = useUpsertServiceCommunity();
    const { data: sections } = useServiceSections();
    const [builderName, setBuilderName] = useState(community?.builder_name ?? '');
    const [name, setName] = useState(community?.name ?? '');
    const [sectionId, setSectionId] = useState(community?.section_id ?? '');
    const [local, setLocal] = useState('');

    function submit() {
        setLocal('');
        save.mutate(
            {
                p_id: community?.id ?? null,
                p_builder_name: builderName,
                p_name: name,
                p_section_id: sectionId || null,
            },
            { onSuccess: onClose, onError: (e) => setLocal(adminErrorMessage(e)) }
        );
    }

    useEffect(() => {
        if (local) onError('');
    }, [local, onError]);

    const preview = [builderName.trim(), name.trim()].filter(Boolean).join(' - ');

    return (
        <Dialog
            open
            onClose={onClose}
            title={community ? 'Edit service community' : 'Add service community'}
            subtitle={community?.display_name}
            footer={
                <>
                    <Button variant="ghost" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={submit} disabled={save.isPending}>
                        {save.isPending ? 'Saving' : 'Save service community'}
                    </Button>
                </>
            }
        >
            {local && <ErrorBanner message={local} onDismiss={() => setLocal('')} />}
            <Field label="Builder">
                <Input
                    value={builderName}
                    onChange={(e) => setBuilderName(e.target.value)}
                    autoFocus
                />
            </Field>
            <Field label="Name">
                <Input value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="Route section" hint="Which section of the route this falls in.">
                <Select value={sectionId} onChange={(e) => setSectionId(e.target.value)}>
                    <option value="">No section</option>
                    {(sections ?? []).map((s) => (
                        <option key={s.id} value={s.id}>
                            {s.name}
                        </option>
                    ))}
                </Select>
            </Field>
            {preview && (
                <p className="text-xs text-graphite-500 dark:text-graphite-500">
                    Shows on the service board as {preview}
                </p>
            )}
        </Dialog>
    );
}
